import { useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { NavLink, useLocation } from "react-router-dom";

import "./meta-rating-landing-page.css";

export default function App() {
  const metaRatingLandingPageAssetsUrl =
    import.meta.env.VITE_APP_ASSETS_BASE_URL + "/fc24/meta-rating-landing-page";

  const sectionRef = useRef(null);
  const { hash } = useLocation();

  useEffect(() => {
    if (hash?.startsWith("#meta-tiers")) {
      window.scrollTo({
        top: sectionRef.current.offsetTop,
        behavior: "smooth",
      });
    }
  }, [hash]);

  return (
    <div className="overflow-hidden bg-[#070D0D]">
      <Helmet>
        <title>EA FC 25 Meta Ratings - EasySBC</title>
        <link rel="canonical" href="https://www.easysbc.io/meta-rating" />
        <meta
          name="description"
          content="Learn why Meta Ratings let you see how any FC 25 card performs in-game. Developed with pros, Meta Ratings tell you all you need to know with a single number."
        />
      </Helmet>

      <div className="bg-black max-w-[1680px] mx-auto">
        {/* Hero Section */}
        <div className="relative max-w-[1680px] mx-auto px-5 h-[300px] sm2:h-[340px] md-metarating:h-[370px] lg-metarating:h-[440px] overflow-hidden bg-[#070707] z-10">
          <img
            src={metaRatingLandingPageAssetsUrl + "/LP_Desktop.jpg"}
            className="absolute top-0 right-0 hidden object-cover h-full md-metarating:flex"
            alt={"lp-desktop"}
          />
          <img
            src={metaRatingLandingPageAssetsUrl + "/LP_Mobile.jpg"}
            alt={"lp-mobile"}
            className="absolute top-0 right-0 flex object-cover h-full md-metarating:hidden"
          />
          <div className="relative z-20 translate-y-[30px] sm2:translate-y-[60px] md-metarating:translate-y-[80px] lg-metarating:ml-20">
            <h1 className="pt-4 font-bold text-[30px] sm3:text-[35px] sm2:text-[45px] md-metarating:text-[60px] lg-metarating:text-[70px] text-white leading-snug">
              Meta Ratings
            </h1>
            <h2 className="font-semiboldtext-[15px] mobile:text-[18px] max-w-[590px] mb-2 text-[#d9d9d9]">
              AI-powered player ratings, updated every patch by FC champions and
              AI engineers.
            </h2>
          </div>
        </div>

        {/* 2nd Section */}
        <div className="relative max-w-[1680px] mx-auto bg-[#121212] w-full z-20">
          <div
            className="max-w-[1680px] mx-auto py-5 px-5 min-h-[300px] bg-[#121212]"
            style={{
              boxShadow: "inset 0px 4.4541px 1.9089px #593F37",
            }}
          >
            <div className="font-bold text-white text-[25px] mb-2 lg-metarating:ml-20 md-xl:translate-y-5 large-desktop:translate-y-10">
              Performance Explained in One Number
            </div>
            <div className="font-medium text-new-gray-600 text-[15px] mobile:text-[18px] max-w-[590px] mb-2 lg-metarating:ml-20 md-xl:translate-y-5 large-desktop:translate-y-10">
              Uncover in-game performance of any card or evolution. Meta Ratings
              reveals a card's true on-pitch ability, ensuring you never waste
              coins on underperformers again.
            </div>

            <div className="sm5:flex items-center lg-metarating:justify-end lg-metarating:translate-y-[155px] md-metarating:mt-[150px] lg-metarating:mt-0">
              <div className="flex flex-col gap-10 md-metarating:items-end md-metarating:flex-row ">
                <div>
                  <img
                    src={metaRatingLandingPageAssetsUrl + "/logo.svg"}
                    alt="logo"
                    className="w-[110px] mobile:w-[230px] lg-metarating:scale-[1.2] lg-metarating:-translate-y-10 ml-auto mobile:ml-0"
                  />
                </div>
                <div>
                  <div className="grid grid-cols-3 sm5:flex items-center gap-5 sm4:gap-8 mt-[180px] mobile:mt-0">
                    <div className="relative customShadow rounded-lg border-2 border-[#FF8100] sm5:w-[120px] sm4:w-[150px] flex items-center justify-center h-[70px]">
                      <div className="relative flex flex-col items-center">
                        <img
                          className="max-w-[120px] mobile:max-w-[160px] large-desktop:max-w-[200px] absolute bottom-0 pb-16 mx-auto"
                          src={metaRatingLandingPageAssetsUrl + "/renard2.png"}
                          alt={"renard"}
                        />
                        <div className="relative text-white font-bold text-[30px] sm5:text-[40px] sm4:text-[50px] z-10">
                          63.7
                        </div>
                      </div>
                      <img
                        src={metaRatingLandingPageAssetsUrl + "/bg2.svg"}
                        className="absolute top-0 left-0 w-full h-full"
                        alt="bg"
                      />
                    </div>

                    <div className="relative customShadow rounded-lg border-2 border-[#FF8100] sm5:w-[120px] sm4:w-[150px] flex items-center justify-center h-[70px]">
                      <div className="relative flex flex-col items-center">
                        <img
                          className="mobile:max-w-[160px] large-desktop:max-w-[200px] max-w-[120px] absolute bottom-0 pb-16 mx-auto"
                          src={metaRatingLandingPageAssetsUrl + "/kane2.png"}
                          alt={"kane"}
                        />
                        <div className="relative text-white font-bold text-[30px] sm5:text-[40px] sm4:text-[50px] z-10">
                          71.6
                        </div>
                      </div>
                      <img
                        src={metaRatingLandingPageAssetsUrl + "/bg2.svg"}
                        className="absolute top-0 left-0 w-full h-full"
                        alt="bg"
                      />
                    </div>

                    <div className="relative customShadow rounded-lg border-2 border-[#FF8100] sm5:w-[120px] sm4:w-[150px] flex items-center justify-center h-[70px]">
                      <div className="relative flex flex-col items-center">
                        <img
                          className="mobile:max-w-[160px] large-desktop:max-w-[200px] max-w-[120px] absolute bottom-0 pb-16 mx-auto"
                          src={metaRatingLandingPageAssetsUrl + "/dembele2.png"}
                          alt={"dembele"}
                        />
                        <div className="relative text-white font-bold text-[30px] sm5:text-[40px] sm4:text-[50px] z-10">
                          80.0
                        </div>
                      </div>
                      <img
                        src={metaRatingLandingPageAssetsUrl + "/bg2.svg"}
                        className="absolute top-0 left-0 w-full h-full"
                        alt="bg"
                      />
                    </div>
                  </div>

                  {/* for desktop */}
                  <div className="relative hidden lg-metarating:block">
                    <div className="relative semiContainer w-full flex items-center justify-center text-center text-[#D9D9D9] py-3 border-t-4 border-[#FF8100] mt-5">
                      <div className="max-w-[400px] pl-[50px] mx-auto">
                        <span className="inline font-bold">Meta Rating</span>{" "}
                        scores players from 1 to 99.9 based on how they perform
                        in-game
                      </div>
                    </div>
                    <div className="absolute bottom-0 -left-[296px] w-[300px] h-1 bg-[#FF8100]"></div>
                    <div className="absolute top-7 translate-y-2 -translate-x-4 left-0 -rotate-[53deg] w-[90px] h-1 bg-[#FF8100]"></div>
                  </div>

                  {/* for mobile */}
                  <div className="absolute top-[calc(100%-20px)]  right-0 semiContainer2 bg-[#262626] w-fit lg-metarating:hidden flex items-center justify-center text-center text-[#BABABA] pl-0 pr-3 sm5:pl-3 sm5:pr-5 py-3 mt-5">
                    <div className="max-w-[400px] pl-[30px] sm5:pl-[50px] mx-auto">
                      <span className="inline font-bold">Meta Rating</span>{" "}
                      scores players from 1 to 99.9 based on how they perform
                      in-game
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-[700px] -mt-[300px] absolute left-0 z-[-10]"
            fill="none"
            viewBox="0 0 1422 1060"
          >
            <path
              fill="url(#paint0_radial_401_120)"
              fillOpacity="0.5"
              d="M-133.557.903H1421.56V1059.07H-133.557V.903z"
            ></path>{" "}
            <defs>
              {" "}
              <radialGradient
                id="paint0_radial_401_120"
                cx="0"
                cy="0"
                r="1"
                gradientTransform="matrix(0 938.543 -777.559 0 192.548 243.652)"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#FFA450"></stop>
                <stop offset="1" stopColor="#FF5700" stopOpacity="0"></stop>
              </radialGradient>
            </defs>
          </svg>
        </div>
        <div className="w-full h-[200px]"></div>

        <div className="-mt-[250px] lg-metarating:mt-0">
          <div className="px-5">
            <div className="text-center text-white font-bold text-[25px] px-5 relative z-10">
              Explore Meta Ratings for Any Card
            </div>

            <NavLink
              className="relative mx-auto text-center flex items-center justify-center text-white w-[250px] h-[70px] mt-10 z-30 cursor-pointer"
              to={"/players"}
            >
              <span className="relative text-[19px] font-semibold italic z-10">
                Go to Players
              </span>
              <img
                src={metaRatingLandingPageAssetsUrl + "/bg3.svg"}
                alt="bg"
                className="absolute top-0 left-0 object-cover w-full h-full"
              />
            </NavLink>
          </div>

          <div className="relative mt-[100px] lg-metarating:mt-0 max-w-[1680px] mx-auto z-[500px]">
            <div className="relative h-[900px] md-metarating:h-[1200px] max-w-[1680px] mx-auto flex items-center justify-center w-full">
              <div className="bg-new-gray-950 bg-opacity-90 max-w-[1100px] mx-auto w-full px-7 py-5 border-t-2 border-[#FF8100] relative z-20 -mt-[250px] md-metarating:-mt-[100px]">
                <div className="font-bold text-white text-[20px] mb-2">
                  Updated Every Patch By Professionals
                </div>
                <div className="text-[#A7A7A7] max-w-[650px]">
                  The Meta Rating is developed by FC Champions and AI-engineers.
                  Together we continuously update the algorithm to make sure
                  Meta Ratings align with the current meta.
                </div>
                <div className="mt-8 mx-auto w-full bg-[#131313] border-2 border-[#FF8100] shadow-xs shadow-main">
                  <div className="relative bg-gray-900">
                    {/* Wrapper div for iframe */}
                    <div
                      className="relative"
                      style={{ paddingBottom: "56.25%" }}
                    >
                      <iframe
                        className="absolute top-0 left-0 w-full h-full"
                        src={`https://www.youtube.com/embed/fC2vy6uFlCg`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title="Embedded youtube"
                      />
                    </div>
                  </div>
                </div>
                {/* <div
                  className={`p-[1px] w-[75%] desktop:w-[35%] desktop:absolute desktop:top-[-9%] desktop:left-[60%] large:left-0 large:right-0 mx-auto`}
                > */}
                {/* </div> */}
              </div>

              <img
                src={metaRatingLandingPageAssetsUrl + "/bg6_4.svg"}
                alt="bg"
                className="absolute -top-[120px] lg-metarating:top-0 left-0 w-full h-full xl-metarating:h-auto object-none"
              />
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-[700px] absolute -top-20 right-0 z-10"
              fill="none"
              viewBox="0 0 1205 1639"
            >
              <path
                fill="url(#paint0_radial_635_164)"
                fillOpacity="0.3"
                d="M7.327 14.317L1204.2 0l-7.33 1624.16L0 1638.47 7.327 14.317z"
              ></path>
              <defs>
                <radialGradient
                  id="paint0_radial_635_164"
                  cx="0"
                  cy="0"
                  r="1"
                  gradientTransform="rotate(148.209 386.682 520.278) scale(729.519 803.803)"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#FF7236" stopOpacity="0.87"></stop>
                  <stop offset="1" stopColor="#FF5700" stopOpacity="0"></stop>
                </radialGradient>
              </defs>
            </svg>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-[700px] absolute -bottom-20 left-0 z-10"
              fill="none"
              viewBox="0 0 1205 1639"
            >
              <path
                fill="url(#paint0_radial_635_165)"
                fillOpacity="0.3"
                d="M7.327 14.317L1204.2 0l-7.33 1624.16L0 1638.47 7.327 14.317z"
              ></path>
              <defs>
                <radialGradient
                  id="paint0_radial_635_165"
                  cx="0"
                  cy="0"
                  r="1"
                  gradientTransform="rotate(34.679 -1095.824 716.779) scale(715.725 900.057)"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#FF7236" stopOpacity="0.87"></stop>
                  <stop offset="1" stopColor="#FF5700" stopOpacity="0"></stop>
                </radialGradient>
              </defs>
            </svg>
          </div>

          <div className="relative z-30 max-w-[1680px] mx-auto px-5 -mt-[100px] sm-metarating:-mt-[50px] lg-metarating:mt-0 lg-metarating:mr-32">
            <div className="flex lg-metarating:items-end lg-metarating:justify-end">
              <div>
                <div className="lg-metarating:text-right font-bold text-white text-[22px] mb-3">
                  Cards are rated per role
                </div>
                <div className="lg-metarating:text-justify text-new-gray-600 max-w-[590px]">
                  Target Forwards thrive on height and physicality; poachers on
                  speed and shooting. That's why Meta Ratings are always
                  specific to a Player Role. Leveraging every card detail
                  —PlayStyles, in-game attributes, body types, and more — our AI
                  simplifies choosing the perfect target forward or poacher for
                  your team.
                </div>
              </div>
            </div>

            <div className="mt-20 sm7:mt-36"></div>
          </div>

          <div className="relative max-w-[1680px] mx-auto w-full z-20 -mt-10 sm-metarating:mt-0">
            <div className="relative z-10 w-full bg-[#121212]">
              <div className="max-w-[1680px] mx-auto py-7 px-5">
                <div className="font-bold text-white text-[25px] text-center  mb-2">
                  Find hidden gems and dominate Weekend League
                </div>
                <div className="font-medium text-new-gray-600 text-[15px] max-w-[600px] mx-auto text-center">
                  Explore our Meta Ratings database if you are looking for a new
                  player for your team or go all the way and build your next
                  dream squad with our AI Squad Builder.
                </div>
                <div className="flex flex-col items-center justify-center mt-10 sm-metarating:flex-row gap-7">
                  <NavLink
                    className="relative text-center flex items-center justify-center text-white w-[250px] h-[70px] z-10"
                    to={"/evolutions"}
                  >
                    <span className="relative text-[19px] font-semibold italic z-10">
                      Go to Evolutions
                    </span>
                    <img
                      src={metaRatingLandingPageAssetsUrl + "/bg3.svg"}
                      alt="bg"
                      className="absolute top-0 left-0 object-cover w-full h-full"
                    />
                  </NavLink>

                  <NavLink
                    className="relative text-center flex items-center justify-center text-white w-[250px] h-[70px] z-10"
                    to={"/squad-builder"}
                  >
                    <span className="relative text-[19px] font-semibold italic z-10">
                      Go to Squad Builder
                    </span>
                    <img
                      src={metaRatingLandingPageAssetsUrl + "/bg3.svg"}
                      alt="bg"
                      className="absolute top-0 left-0 object-cover w-full h-full"
                    />
                  </NavLink>
                </div>
              </div>
            </div>
            <svg
              className="absolute -top-[450px] -left-10 pointer-events-none"
              width="1200"
              height="1200"
              viewBox="0 0 1741 1862"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0 0H1740.28V1861.18H0V0Z"
                fill="url(#paint0_radial_649_160)"
                fillOpacity="0.3"
              />
              <defs>
                <radialGradient
                  id="paint0_radial_649_160"
                  cx="0"
                  cy="0"
                  r="1"
                  gradientUnits="userSpaceOnUse"
                  gradientTransform="translate(606.153 964.577) scale(948.087 718.267)"
                >
                  <stop stopColor="#FF8E36" stopOpacity="0.87" />
                  <stop offset="1" stopColor="#FF5700" stopOpacity="0" />
                </radialGradient>
              </defs>
            </svg>
          </div>
          <div
            className="w-full h-auto bg-[#000] flex flex-col items-center"
            ref={sectionRef}
          >
            <section className="flex flex-col items-center p-4 touch-device:p-12 lg-metarating:flex-row gap-x-6 scroll-mt-20">
              <div className="flex flex-col text-white lg-metarating:ml-10 gap-y-2">
                <h2 className="text-2xl font-bold xl-metarating:text-3xl text">
                  Meta Tiers
                </h2>
                <p className=" text-new-gray-600 xl-metarating:w-[587px] font-medium w-full touch-device:w-[450px] xl-metarating:text-[16px]">
                  Meta Tiers are the quickest and easiest way to understand a
                  card’s quality relative to all other cards currently
                  available. They are updated dynamically when new cards come
                  out and always kept up-to-date. While Meta Ratings are great
                  for a more precise comparison of individual players, Meta
                  Tiers make it possible to instantly get an idea of where a
                  player is in the current power curve of the game.
                </p>
              </div>
              <img
                src={metaRatingLandingPageAssetsUrl + "/meta_tiers.png"}
                className="w-[450px] xl-metarating:w-[600px]"
                alt="meta_tiers"
              />
            </section>
            <section className="grid grid-cols-2 w-full lg-metarating:w-[950px] xl-metarating:w-[1200px] lg-metarating:grid-cols-3 p-4 touch-device:p-12 text-[#D9D9D9] gap-x-2 touch-device:gap-x-6 gap-y-6 touch-device:gap-y-10">
              <article className="flex flex-col gap-y-1">
                <div
                  className="text-xl font-bold text-center rounded-t-lg touch-device:text-3xl"
                  style={{
                    background:
                      "radial-gradient(104.84% 81.11% at 48.89% 50.13%, rgba(0, 194, 255, 0.00) 0%, rgba(0, 194, 255, 0.72) 82.23%)",
                  }}
                >
                  S
                </div>
                <div
                  className="h-full p-3"
                  style={{
                    background:
                      "linear-gradient(180deg, #2E2E2E 0%, #1E1E1E 100%)",
                    boxShadow:
                      "-1px -1px 1px 0px rgba(0, 0, 0, 0.30) inset, 1px 1px 1px 0px rgba(255, 255, 255, 0.10) inset",
                  }}
                >
                  Includes the little handful of cards that are superior to all
                  others and are the very best you can get your hands on. (Top
                  0.5%)
                </div>
              </article>
              <article className="flex flex-col gap-y-1">
                <div
                  className="text-xl font-bold text-center rounded-t-lg touch-device:text-3xl"
                  style={{
                    background:
                      "radial-gradient(104.84% 81.11% at 48.89% 50.13%, rgba(26, 178, 92, 0.00) 0%, rgba(26, 178, 92, 0.72) 82.23%)",
                  }}
                >
                  A+
                </div>
                <div
                  className="h-full p-3"
                  style={{
                    background:
                      "linear-gradient(180deg, #2E2E2E 0%, #1E1E1E 100%)",
                    boxShadow:
                      "-1px -1px 1px 0px rgba(0, 0, 0, 0.30) inset, 1px 1px 1px 0px rgba(255, 255, 255, 0.10) inset",
                  }}
                >
                  Great cards that are missing just a few details to be world
                  class. (Top 1.5%)
                </div>
              </article>
              <article className="flex flex-col gap-y-1">
                <div
                  className="text-xl font-bold text-center rounded-t-lg touch-device:text-3xl"
                  style={{
                    background:
                      "radial-gradient(104.84% 81.11% at 48.89% 50.13%, rgba(0, 244, 62, 0.00) 0%, rgba(0, 244, 62, 0.72) 82.23%)",
                  }}
                >
                  A
                </div>
                <div
                  className="h-full p-3"
                  style={{
                    background:
                      "linear-gradient(180deg, #2E2E2E 0%, #1E1E1E 100%)",
                    boxShadow:
                      "-1px -1px 1px 0px rgba(0, 0, 0, 0.30) inset, 1px 1px 1px 0px rgba(255, 255, 255, 0.10) inset",
                  }}
                >
                  Cards you can expect a good performance from but who are
                  likely to come up short if matched against a card from A+ or
                  S. (Top 3%)
                </div>
              </article>
              <article className="flex flex-col gap-y-1">
                <div
                  className="text-xl font-bold text-center rounded-t-lg touch-device:text-3xl"
                  style={{
                    background:
                      "radial-gradient(104.84% 81.11% at 48.89% 50.13%, rgba(255, 225, 5, 0.00) 0%, rgba(255, 225, 5, 0.72) 82.23%)",
                  }}
                >
                  B+
                </div>
                <div
                  className="h-full p-3"
                  style={{
                    background:
                      "linear-gradient(180deg, #2E2E2E 0%, #1E1E1E 100%)",
                    boxShadow:
                      "-1px -1px 1px 0px rgba(0, 0, 0, 0.30) inset, 1px 1px 1px 0px rgba(255, 255, 255, 0.10) inset",
                  }}
                >
                  A decent card which can be used but lacks in key areas
                  compared to cards from higher tiers. (Top 5%)
                </div>
              </article>
              <article className="flex flex-col gap-y-1">
                <div
                  className="text-xl font-bold text-center rounded-t-lg touch-device:text-3xl"
                  style={{
                    background:
                      "radial-gradient(104.84% 81.11% at 48.89% 50.13%, rgba(253, 176, 34, 0.00) 0%, rgba(253, 176, 34, 0.72) 82.23%)",
                  }}
                >
                  B
                </div>
                <div
                  className="h-full p-3"
                  style={{
                    background:
                      "linear-gradient(180deg, #2E2E2E 0%, #1E1E1E 100%)",
                    boxShadow:
                      "-1px -1px 1px 0px rgba(0, 0, 0, 0.30) inset, 1px 1px 1px 0px rgba(255, 255, 255, 0.10) inset",
                  }}
                >
                  An acceptable card that will do OK but has significant
                  shortcomings. (Top 10%)
                </div>
              </article>
              <article className="flex flex-col gap-y-1">
                <div
                  className="text-lg font-bold text-center rounded-t-lg touch-device:pb-1 touch-device:text-xl"
                  style={{
                    background:
                      "radial-gradient(104.84% 81.11% at 48.89% 50.13%, rgba(255, 0, 0, 0.06) 0%, rgba(252, 136, 0, 0.72) 82.23%)",
                  }}
                >
                  <div className="touch-device:mt-1">Unranked</div>
                </div>
                <div
                  className="h-full p-3"
                  style={{
                    background:
                      "linear-gradient(180deg, #2E2E2E 0%, #1E1E1E 100%)",
                    boxShadow:
                      "-1px -1px 1px 0px rgba(0, 0, 0, 0.30) inset, 1px 1px 1px 0px rgba(255, 255, 255, 0.10) inset",
                  }}
                >
                  Cards that are overdue for an upgrade.
                </div>
              </article>
            </section>
          </div>
          {/* <div className="relative flex items-end justify-center md-metarating:mt-[50px]">
            <img
              src={metaRatingLandingPageAssetsUrl + "/bg5.svg"}
              alt="bg"
              className="w-[900px] relative z-10"
            />
            <svg
              className="w-[800px] h-[800px] md-metarating:w-[1155px] md-metarating:h-[1155px] absolute -bottom-[calc(50%+350px)] sm6:-bottom-[calc(50%+270px)] left-1/2 -translate-x-1/2"
              viewBox="0 0 1704 1055"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0 0H1703.38V1054.35H0V0Z"
                fill="url(#paint0_radial_678_3)"
                fillOpacity="0.5"
              />
              <defs>
                <radialGradient
                  id="paint0_radial_678_3"
                  cx="0"
                  cy="0"
                  r="1"
                  gradientUnits="userSpaceOnUse"
                  gradientTransform="translate(930.096 636.106) rotate(89.3489) scale(613.37 769.688)"
                >
                  <stop stopColor="#FFA450" />
                  <stop offset="1" stopColor="#FF5700" stopOpacity="0" />
                </radialGradient>
              </defs>
            </svg>
          </div> */}

          <div className="bg-[#121212] relative w-full z-20 max-w-[1680px] mx-auto ">
            <div className="max-w-[1680px] mx-auto flex items-center justify-center flex-col gap-10 text-center py-14 px-5">
              <div className="font-bold text-white text-[25px]">
                Want to join our community or simply give us feedback?
              </div>

              <a
                className="relative text-center flex items-center justify-center text-white w-[250px] h-[70px] z-10"
                href={"https://discord.gg/mmNtdnUcJf"}
                target="_blank"
                rel="noreferrer"
              >
                <span className="relative text-[19px] font-semibold italic z-10">
                  Join our Discord
                </span>
                <img
                  src={metaRatingLandingPageAssetsUrl + "/bg3.svg"}
                  alt="bg"
                  className="absolute top-0 left-0 object-cover w-full h-full"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
